import DOMPurify from "dompurify";
import { Renderer, marked } from "marked";

import "./markdown.scss";

const renderer = new Renderer();

function link(this: Renderer, href: string | null, title: string | null, text: string): string {
  if (!href) return text;

  const isExternal = !href.startsWith("/") && !href.startsWith("#");
  const hasProtocol = /^([a-z][a-z0-9+\-.]*:)?\/\//i.test(href);
  const finalHref = isExternal && !hasProtocol ? `//${href}` : href;

  const titleAttr = title ? ` title="${title}"` : "";
  return `<a href="${finalHref}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
}

renderer.link = link;

/**
  We get most of our markdown from CB's RichTextEditor.
  There are two non-standard behaviors from CB's markdown editor that we need to account for:
  - It introduces frequent spaces before or after markers like "_Italic _".
  - "++" is usually not used as underline.

  The mapping from course-builder markdown editor to html should be:
  **Bold** to <strong>, which is styled automatically
  _Italic_ to <em>, which we style in CSS to be italic
  ++Underline++ to <span style="text-decoration: underline">, which is style by the inline styles
  ```Code``` to <pre><code>Code</code></pre>
  > Block to <blockquote>
  # Header to <h1>, ## to <h2>, and so on
  - List to <ul>
  1. Numbered list to <ol>

  Most of the special handling in this file is copied from client/app/javascripts/app/helpers.js:markdownify
  and adapted to work with the "marked" instead of the "remarkable" library.
  */
export const safeMarkdownToHtml = (str: string) => {
  // Remove preceding or trailing whitespace that might be breaking formatting.
  // See https://regex101.com/r/cBrHBI/1
  const markdown = str
    .replace(/\+\+( *)([^+]*?)( *)\+\+/g, "$1++$2++$3")
    .replace(/\*\*( *)([^*]*?)( *)\*\*/g, "$1**$2**$3")
    .replace(/_( *)([^_]*?)( *)_/g, "$1_$2_$3");

  let html = DOMPurify.sanitize(marked.parse(markdown, { renderer }), {
    ADD_ATTR: ["target", "title", "rel", "style"],
  });
  // Manually convert underlines
  html = html.replace(/\+\+([\S\s]+?)\+\+/g, '<span style="text-decoration: underline;">$1</span>');
  // We add the styling here to make sure the downstream implementation can't forget to add it.
  html = `<div class="markdown">${html}</div>`;
  return { __html: html };
};
